import {Component, computed, effect, inject, signal} from '@angular/core';
import {CommonModule} from '@angular/common';
import {TranslocoPipe} from '@jsverse/transloco';
import {ButtonModule} from 'primeng/button';
import {finalize} from 'rxjs';
import {ProjectStepBaseReactive} from '../../../project-base-step/project-base-step-reactive.component';
import {DecisionReportControllerService, DecisionReportExportStatusDto} from '@frontend/shared-api';
import {ProjectChapterType, ProjectStepType} from '@frontend/shared';
import {FileDownloadService} from '../../../../../../shared/utils/file-download.service';
import {PreviousStepIncompleteNoticeComponent} from '../../../../../../shared/components/previous-step-incomplete-notice/previous-step-incomplete-notice.component';

@Component({
  selector: 'app-step-10-1-decision-report',
  imports: [CommonModule, TranslocoPipe, ButtonModule, PreviousStepIncompleteNoticeComponent],
  templateUrl: './step-10-1-decision-report.component.html',
  styleUrl: './step-10-1-decision-report.component.scss',
})
export class Step101DecisionReportComponent extends ProjectStepBaseReactive {
  private readonly decisionReportService = inject(DecisionReportControllerService);
  private readonly fileDownloadService = inject(FileDownloadService);

  protected readonly isGenerating = signal(false);
  protected readonly isDownloading = signal(false);
  protected readonly exportStatus = signal<DecisionReportExportStatusDto | undefined>(undefined);

  protected readonly hasGeneratedReport = computed(() => this.exportStatus()?.generated === true);

  protected readonly isStepPrerequisiteCompleted = computed(() =>
    this.stepState.areStepsCompleted([
      {
        chapterId: ProjectChapterType.RFQ,
        stepId: ProjectStepType.RFQ_AGGREGATED_RESULTS,
      },
    ]),
  );

  constructor() {
    super();
    effect(() => {
      const projectId = this.projectId();
      if (projectId !== undefined && this.isStepPrerequisiteCompleted()) {
        this.loadStatus(projectId);
      }
    });
  }

  protected generateReport(): void {
    const projectId = this.projectId();
    if (projectId === undefined) return;

    this.isGenerating.set(true);
    this.decisionReportService
      .generateReport({projectId}, 'response')
      .pipe(finalize(() => this.isGenerating.set(false)))
      .subscribe({
        next: (response) => {
          this.fileDownloadService.downloadFromResponse(response);
          this.loadStatus(projectId);
        },
        error: (err) => console.error('Error generating decision report:', err),
      });
  }

  protected downloadReport(): void {
    const projectId = this.projectId();
    if (projectId === undefined) return;

    this.isDownloading.set(true);
    this.decisionReportService
      .downloadReport({projectId}, 'response')
      .pipe(finalize(() => this.isDownloading.set(false)))
      .subscribe({
        next: (response) => this.fileDownloadService.downloadFromResponse(response),
        error: (err) => console.error('Error downloading decision report:', err),
      });
  }

  private loadStatus(projectId: number): void {
    this.decisionReportService.downloadStatus({projectId}).subscribe({
      next: (status) => this.exportStatus.set(status),
      error: (err) => console.error('Error loading decision report status:', err),
    });
  }
}
